import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Trash2, Plus } from "lucide-react";


const AddEditComment = ({
  totalQuestions,
  askComments,
  setAskComments,
  updated,
  setUpdated,
  UpdateComments,
}) => {
  const [pristine, setPristine] = useState(true);
  const [listQuestion, setListQuestion] = useState("");
  const [listQuestionFr, setListQuestionFr] = useState("");
  const [formErrors, setFormErrors] = useState([]);

  const onSubmit = () => {
    if (formErrors.length > 0) {
      setPristine(false);
      return;
    }
    setPristine(true);
    const next = Array.isArray(askComments) ? [...askComments] : [];
    
    // Find the highest order number to ensure proper ordering
    const maxOrder = next.length > 0 
      ? Math.max(...next.map(c => c.order || 0))
      : totalQuestions;
    
    next.push({
      id: uuidv4(),
      question: listQuestion,
      fr: listQuestionFr,
      order: maxOrder + 1,
    });
    setAskComments(next);
    UpdateComments(next); // Pass the new array directly to avoid closure issues
    setListQuestion("");
    setListQuestionFr("");
    setUpdated(Math.random());
  };

  useEffect(() => {
    const errors = [];
    if (!listQuestion.trim()) {
      errors.push("Enter a question (English)");
    }
    setFormErrors(errors);
  }, [listQuestion, updated]);

  const removeFromList = (id) => {
    const next = (askComments || []).filter((item) => item.id !== id);
    setAskComments(next);
    UpdateComments(next); // Pass the new array directly
    setUpdated(Math.random());
  };

  const editDetails = (id, name, value) => {
    const target = (askComments || []).find((item) => item.id === id);
    if (!target) return;
    const rest = (askComments || []).filter((item) => item.id !== id);
    const updatedItem = { ...target, [name]: value };
    const next = [...rest, updatedItem].sort((a, b) => a.order - b.order);
    setAskComments(next);
    UpdateComments(next); // Pass the new array directly
    setUpdated(Math.random());
  };

  const renderErrors = () => {
    if (formErrors.length === 0 || pristine) return null;
    return (
      <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-semibold">We need these details!</p>
        <ul className="ml-4 list-disc space-y-1">
          {formErrors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Card className="border-border/70">
      <CardHeader>
        <CardTitle className="text-base font-semibold">Comment questions</CardTitle>
        <CardDescription>
          Manage follow-up comment prompts and their order.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border">
          <div className="max-h-[50vh] overflow-y-auto">
            <Table className="min-w-full">
              <TableHeader className="bg-muted/40 sticky top-0 z-10">
              <TableRow>
                <TableHead className="w-[100px]">Order</TableHead>
                <TableHead>Question (EN)</TableHead>
                <TableHead>Question (FR)</TableHead>
                <TableHead className="w-[60px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(askComments || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    No comment questions yet.
                  </TableCell>
                </TableRow>
              ) : (
                askComments.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        value={item?.order ?? ""}
                        onChange={(event) => editDetails(item.id, "order", event.target.value)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <Textarea
                        value={item?.question ?? ""}
                        onChange={(event) => editDetails(item.id, "question", event.target.value)}
                        placeholder="English question"
                        rows={2}
                      />
                    </TableCell>
                    <TableCell>
                      <Textarea
                        value={item?.fr ?? ""}
                        onChange={(event) => editDetails(item.id, "fr", event.target.value)}
                        placeholder="French translation"
                        rows={2}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFromList(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </div>

        <div>
          {renderErrors()}
          <div className="rounded-md bg-muted/10 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  New comment question (EN)
                </label>
                <Textarea
                  value={listQuestion}
                  onChange={(event) => setListQuestion(event.target.value)}
                  placeholder="Enter English question"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  French translation (FR)
                </label>
                <Textarea
                  value={listQuestionFr}
                  onChange={(event) => setListQuestionFr(event.target.value)}
                  placeholder="Enter French translation"
                  rows={3}
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Comment questions appear after the main question set. Order values are applied automatically.
            </p>
            <div className="mt-4 flex justify-end">
              <Button type="button" className="md:w-40" onClick={onSubmit}>
                <Plus className="mr-2 h-4 w-4" /> Add comment
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddEditComment;
